/**
 * Browser microphone discovery.
 *
 * Browser quirk: `enumerateDevices()` returns entries with empty `label`
 * fields until the origin has been granted mic access at least once.
 * The discovery flow has to call `getUserMedia({audio: true})` first
 * (releasing the probe stream immediately) so the subsequent enumerate
 * call has labels to show in a picker.
 *
 * Permission state is queried via the `permissions` API where available
 * — Safari historically didn't implement it; the helper falls back to
 * "prompt" in that case so the UI shows a "Tap to enable voice" button
 * which triggers the OS prompt naturally on click.
 */

export type MicPermissionState = "prompt" | "granted" | "denied" | "unsupported";

const ACTIVE_MIC_KEY = "mythglade_active_mic_id";

export function isMediaDevicesSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices !== "undefined" &&
    typeof navigator.mediaDevices.getUserMedia === "function"
  );
}

export async function getMicPermissionState(): Promise<MicPermissionState> {
  if (!isMediaDevicesSupported()) return "unsupported";
  if (typeof navigator.permissions?.query !== "function") return "prompt";

  try {
    const status = await navigator.permissions.query({
      name: "microphone" as PermissionName,
    });
    return status.state as MicPermissionState;
  } catch {
    // Some browsers throw on unsupported permission name — treat as prompt.
    return "prompt";
  }
}

/**
 * Triggers the OS mic permission prompt if not already granted, then
 * enumerates audio inputs. Returns labelled devices on grant, or
 * unlabelled placeholders on denial (caller can render an unblock hint).
 */
export async function discoverMics(): Promise<MediaDeviceInfo[]> {
  if (!isMediaDevicesSupported()) return [];

  try {
    const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
    probe.getTracks().forEach((t) => t.stop());
  } catch {
    // Permission denied or no input — enumerate anyway so the caller
    // can show whatever unlabelled entries the browser exposes.
  }

  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === "audioinput");
}

/**
 * Opens a mic stream with the browser's standard processing chain
 * enabled (AEC / NS / AGC). Pass `null` for `deviceId` to use the OS
 * default — that is the right choice when the user has not picked a
 * device explicitly.
 */
export async function openMic(deviceId: string | null): Promise<MediaStream> {
  if (!isMediaDevicesSupported()) {
    throw new Error("mediaDevices not supported");
  }

  const audio: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 48_000,
    channelCount: 1,
  };

  if (deviceId) {
    audio.deviceId = { exact: deviceId };
  }

  return navigator.mediaDevices.getUserMedia({ audio });
}

export function loadActiveMicId(): string | null {
  return localStorage.getItem(ACTIVE_MIC_KEY);
}

export function saveActiveMicId(id: string | null): void {
  if (id === null) {
    localStorage.removeItem(ACTIVE_MIC_KEY);
  } else {
    localStorage.setItem(ACTIVE_MIC_KEY, id);
  }
}

export type DeviceChangeListener = (mics: MediaDeviceInfo[]) => void;

/**
 * Subscribes to hot-plug events (USB headset connect/disconnect).
 * Returns a disposer that removes the listener.
 */
export function onDeviceChange(handler: DeviceChangeListener): () => void {
  if (!isMediaDevicesSupported()) return () => {};

  const wrapped = async (): Promise<void> => {
    const mics = await discoverMics();
    handler(mics);
  };

  navigator.mediaDevices.addEventListener("devicechange", wrapped);
  return () =>
    navigator.mediaDevices.removeEventListener("devicechange", wrapped);
}
